import React, { useState, useEffect } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { getOptimizedImageUrl, resolveImageUrl } from '../../utils/image';
import { getCachedSignedUrl, cacheSignedUrl } from '../../utils/imageCache';

interface SmartImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  alt: string;
  fallbackSrc?: string;
  className?: string;
  showOfflinePlaceholder?: boolean;
  withWatermark?: boolean;
  watermarkSize?: 'small' | 'large';
}

const SmartImage = React.forwardRef<HTMLImageElement, SmartImageProps>(({
  src,
  alt,
  fallbackSrc,
  className = '',
  showOfflinePlaceholder = true,
  withWatermark = false,
  watermarkSize = 'small',
  ...props
}, ref) => {
  const localRef = React.useRef<HTMLImageElement>(null);
  const [imgSrc, setImgSrc] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!src) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    // Try to get a valid signed URL from cache to avoid cache-busting by fresh signatures
    const cachedUrl = getCachedSignedUrl(src);
    const urlToUse = cachedUrl || src;
    
    // If this is a new signed URL we haven't seen, cache it
    if (!cachedUrl && src.includes('?')) {
      cacheSignedUrl(src);
    }

    if (localRef.current && localRef.current.complete && localRef.current.naturalWidth > 0 && localRef.current.src === urlToUse) {
        setIsLoading(false);
    } else {
        setIsLoading(true);
    }

    setHasError(false);
    setImgSrc(urlToUse);

    const pollInterval = setInterval(() => {
        if (localRef.current && localRef.current.naturalWidth > 0) {
            setIsLoading(false);
            clearInterval(pollInterval);
        }
    }, 500);

    const timeoutId = setTimeout(() => {
      if (isLoading) {
        if (localRef.current && localRef.current.naturalWidth > 0) {
             setIsLoading(false);
        } else {
             setIsLoading(false);
             setHasError(true);
        }
      }
    }, 15000);

    return () => {
        clearTimeout(timeoutId);
        clearInterval(pollInterval);
    };
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (fallbackSrc && imgSrc !== fallbackSrc) {
        setImgSrc(fallbackSrc);
    } else {
        setHasError(true);
    }
    setIsLoading(false);
    if (props.onError) {
        props.onError(e);
    }
  };

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoading(false);
    if (props.onLoad) {
        props.onLoad(e);
    }
  };

  if (hasError) {
    if (showOfflinePlaceholder) {
      return (
        <div 
            ref={ref as any}
            className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-400 ${className}`}
            {...props as any}
        >
          <div className="text-center p-2">
            <PhotoIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
            <span className="text-[10px] uppercase tracking-wider font-medium">
                {navigator.onLine ? 'Image N/A' : 'Offline'}
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  const { onLoad, onError, ...restProps } = props;

  const imageElement = (
    <img
      ref={(node) => {
        localRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            (ref as React.MutableRefObject<HTMLImageElement | null>).current = node;
        }
        
        if (node && node.complete && node.naturalWidth > 0) {
            setIsLoading(false);
        }
      }}
      src={imgSrc}
      alt={alt}
      className={`${className} transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
      onError={handleError}
      onLoad={handleLoad}
      {...restProps}
    />
  );

  if (withWatermark) {
    // Determine if we need to apply layout classes to the container
    const containerClasses = className.split(' ').filter(c => 
        c.startsWith('absolute') || 
        c.startsWith('relative') || 
        c.startsWith('inset-') || 
        c.startsWith('h-') || 
        c.startsWith('w-') || 
        c.startsWith('top-') || 
        c.startsWith('left-') || 
        c.startsWith('right-') || 
        c.startsWith('bottom-') ||
        c.startsWith('z-') ||
        c.startsWith('shadow') ||
        c.startsWith('rounded')
    ).join(' ');

    return (
      <div className={`watermark-container ${watermarkSize === 'large' ? 'watermark-large' : ''} ${containerClasses}`}>
        {imageElement}
      </div>
    );
  }

  return imageElement;
});

SmartImage.displayName = 'SmartImage';

export default SmartImage;
